import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Checkbox } from "@/components/ui/Field";
import { toast } from "@/lib/toast";
import {
  getInvoiceTextIds,
  listHeroSupplyTexts,
  saveInvoiceTextIds,
  type HeroSupplyText,
} from "../../lib/settingsApi";

export function InvoiceTextsSection() {
  const [texts, setTexts] = useState<HeroSupplyText[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [supply, ids] = await Promise.all([listHeroSupplyTexts(), getInvoiceTextIds()]);
      setTexts(supply);
      setSelected(new Set(ids));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Rechnungstexte konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  async function handleSave() {
    setSaving(true);
    try {
      await saveInvoiceTextIds([...selected]);
      toast.success("Rechnungstexte gespeichert.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Rechnungstexte"
      description="Hero-Liefertexte für Rechnungsentwürfe auswählen."
      actions={
        <Button type="button" onClick={handleSave} loading={saving}>
          Speichern
        </Button>
      }
    >
      {loading && (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lädt …
        </div>
      )}
      {!loading && (
        <ul className="space-y-2">
          {texts.map((t) => (
            <li key={t.id}>
              <Checkbox
                label={t.name || `Text #${t.id}`}
                description={t.text ? String(t.text).slice(0, 120) : undefined}
                checked={selected.has(t.id)}
                onChange={(e) => {
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(t.id);
                    else next.delete(t.id);
                    return next;
                  });
                }}
              />
            </li>
          ))}
          {texts.length === 0 && (
            <p className="text-sm text-label-secondary">Keine Liefertexte von Hero verfügbar.</p>
          )}
        </ul>
      )}
    </Card>
  );
}
